import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaGithub, FaExternalLinkAlt, FaRobot, FaBook, FaUsers } from 'react-icons/fa';
import { useLanguage } from '../utils/LanguageContext';
import './AllComponents.css';

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const { t } = useLanguage();

  const projects = [
    {
      title: t('projects.projectData.facts4chat.title'),
      description: t('projects.projectData.facts4chat.description'),
      image: "/api/profile/facts4chat.png",
      technologies: ["Python", "AI/ML", "Information Retrieval", "Embeddings", "Git", "Team Collaboration"],
      github: "https://gitlab.fachschaften.org/facts4chat",
      demo: "https://facts4chat.cs.tu-dortmund.de/",
      category: t('projects.projectData.facts4chat.category'),
      icon: <FaRobot />
    },
    {
      title: t('projects.projectData.stubook.title'),
      description: t('projects.projectData.stubook.description'),
      image: "/api/profile/stubook.png",
      technologies: ["MongoDB", "Express.js", "React", "Node.js", "JavaScript", "REST API"],
      github: "https://github.com/ahmadasakra/stubook",
      demo: "https://stubook-demo.example.com",
      category: t('projects.projectData.stubook.category'),
      icon: <FaBook />
    },
    {
      title: t('projects.projectData.planvo.title'),
      description: t('projects.projectData.planvo.description'),
      image: "/api/profile/planvo3.png",
      technologies: ["React", "Node.js", "Database Management", "HR Systems", "API Design", "CI/CD"],
      github: "https://github.com/ahmadasakra/planvo",
      demo: "https://www.planvo.de",
      category: t('projects.projectData.planvo.category'),
      icon: <FaUsers />
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section id="projects" className="section projects-section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div variants={itemVariants} className="section-header text-center">
            <h2 className="gradient-text">{t('projects.featuredProjects')}</h2>
            <p className="section-subtitle">
              {t('projects.projectsSubtitle')}
            </p>
          </motion.div>

          <div className="projects-grid">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                className="project-card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay">
                    <div className="project-links">
                      <a 
                        href={project.github} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="project-link"
                      >
                        <FaGithub />
                      </a>
                      <a 
                        href={project.demo} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="project-link"
                      >
                        <FaExternalLinkAlt />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="project-content">
                  <div className="project-header">
                    <div className="project-category">
                      {project.icon}
                      <span>{project.category}</span>
                    </div>
                  </div>

                  <h3>{project.title}</h3>
                  <p>{project.description}</p>

                  <div className="project-technologies">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="project-actions">
                    <a 
                      href={project.github} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-outline"
                    >
                      <FaGithub />
                      {t('projects.code')}
                    </a>
                    <a 
                      href={project.demo} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      <FaExternalLinkAlt />
                      {t('projects.liveDemo')}
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 